import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * A drop-in replacement for a native `<select>`.
 *
 * Why not the native element: its option list is drawn by the OS, so it ignores the
 * theme entirely — a dark-mode page opened a white list on macOS, and the control's
 * height and font differed on every platform. The arrow could not be styled either,
 * which is what the pages worked around with an absolutely positioned chevron.
 *
 * The API is deliberately the native one — `value`, `onChange(event)` carrying
 * `event.target.value`, and `<option>` children — so replacing a `<select>` is a tag
 * rename and nothing about the page's state handling changes.
 *
 * The chevron is a flex sibling rather than an absolutely positioned overlay: it then
 * follows the writing direction on its own, and cannot land on the wrong side.
 */

/**
 * Radix reserves the empty string for "no selection", so an `<option value="">All</option>`
 * — which most filters here use — cannot be an item value. It travels as this sentinel
 * inside the listbox and is mapped back to `''` at both edges, so callers never see it.
 */
const EMPTY = '__hrms_empty__'

export interface SelectChangeEvent {
  target: { value: string; name?: string }
}

export interface SelectProps {
  value?: string | number
  defaultValue?: string | number
  onChange?: (event: SelectChangeEvent) => void
  name?: string
  id?: string
  disabled?: boolean
  required?: boolean
  /** Shown when the value is empty and no matching `<option value="">` exists. */
  placeholder?: string
  className?: string
  /** Applied to the popover, for the rare caller that needs to widen it. */
  contentClassName?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  children?: React.ReactNode
}

interface ParsedOption {
  value: string
  label: React.ReactNode
  text: string
  disabled?: boolean
}

/** Flattens fragments and `{list.map(...)}` arrays down to the `<option>` elements. */
function collectOptions(children: React.ReactNode, out: ParsedOption[] = []): ParsedOption[] {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return

    if (child.type === React.Fragment) {
      collectOptions((child.props as { children?: React.ReactNode }).children, out)
      return
    }

    if (child.type === 'option') {
      const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement> & {
        children?: React.ReactNode
      }
      out.push({
        value: String(props.value ?? ''),
        label: props.children,
        text: textOf(props.children),
        disabled: props.disabled,
      })
    }
  })
  return out
}

/** The flat text of an option, for the trigger's label and for type-ahead. */
function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (React.isValidElement(node)) {
    return textOf((node.props as { children?: React.ReactNode }).children)
  }
  return ''
}

export function Select({
  value,
  defaultValue,
  onChange,
  name,
  id,
  disabled,
  required,
  placeholder,
  className,
  contentClassName,
  children,
  ...aria
}: SelectProps) {
  const options = React.useMemo(() => collectOptions(children), [children])

  const toItem = (raw: string | number | undefined) =>
    raw === '' || raw === undefined ? EMPTY : String(raw)
  const fromItem = (item: string) => (item === EMPTY ? '' : item)

  // Mirrors the value when the caller does not pass one, exactly as the native element
  // would, so an uncontrolled `<select defaultValue>` keeps working unchanged.
  const [internal, setInternal] = React.useState(() => String(defaultValue ?? ''))
  const current = value === undefined ? internal : String(value)

  // The empty option doubles as the placeholder when the page supplied one ("All",
  // "Choose a person…"), which is why the trigger prefers its label over `placeholder`.
  const selected = options.find((option) => option.value === current)

  // Radix reads direction from a DirectionProvider we do not mount; the app puts it on
  // <html> instead, so the popover is told explicitly or it would open LTR under Arabic.
  const dir =
    typeof document === 'undefined'
      ? 'ltr'
      : document.documentElement.dir === 'rtl'
        ? 'rtl'
        : 'ltr'

  function handleChange(next: string) {
    const plain = fromItem(next)
    if (value === undefined) setInternal(plain)
    onChange?.({ target: { value: plain, name } })
  }

  return (
    <SelectPrimitive.Root
      value={toItem(current)}
      onValueChange={handleChange}
      disabled={disabled}
      dir={dir}
    >
      <SelectPrimitive.Trigger
        id={id}
        data-slot="select-trigger"
        aria-invalid={aria['aria-invalid'] || undefined}
        aria-label={aria['aria-label']}
        aria-labelledby={aria['aria-labelledby']}
        aria-describedby={aria['aria-describedby']}
        className={cn(
          // Sizes to its widest option rather than stretching: a filter row of controls
          // each as wide as its container turns three short lists into three identical
          // boxes. A form grid still asks for `w-full` and gets it, because the class the
          // caller passes wins.
          'inline-flex h-10 w-auto max-w-full items-center justify-between gap-2 rounded-[var(--radius-control)]',
          'border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-start text-sm',
          'text-[var(--color-heading)]',
          'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[var(--color-primary)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[placeholder]:text-[var(--color-muted)]',
          'aria-invalid:border-[var(--color-danger)]',
          className,
        )}
      >
        <span className="grid min-w-0 flex-1">
          <span
            className={cn(
              'col-start-1 row-start-1 truncate text-start',
              current === '' && 'text-[var(--color-muted)]',
            )}
          >
            {selected ? selected.label : (placeholder ?? '')}
          </span>
          {/*
           * The width of the widest option, taking no height and no space in the
           * accessibility tree. Without it the control would resize as the reader picks
           * different options, which makes a whole filter row shift under the pointer.
           */}
          <span aria-hidden="true" className="col-start-1 row-start-1 h-0 overflow-hidden">
            {options.map((option) => (
              <span key={option.value} className="block whitespace-nowrap">
                {option.text || placeholder || ''}
              </span>
            ))}
          </span>
        </span>
        {/* Last flex child, so it is on the trailing edge in both LTR and RTL. */}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      {/*
       * The control the form sees. Radix renders a hidden select of its own, but it can
       * only hold one of the listbox's item values — and "nothing chosen" is the empty
       * string, which Radix reserves and so cannot store. That made `required` pass on a
       * field nobody had answered. This one holds the value the page actually has, so
       * `required` refuses an empty field exactly as the native element did, and a form
       * submitted the old way still carries the field.
       */}
      <select
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        required={required}
        disabled={disabled}
        value={current}
        onChange={() => {}}
        className="pointer-events-none absolute size-px overflow-hidden opacity-0"
      >
        <option value="" />
        {options.map((option, index) => (
          <option key={`native-${option.value}-${index}`} value={option.value} />
        ))}
      </select>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-50 max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)]',
            'overflow-hidden rounded-[var(--radius-control)] border border-[var(--color-border)]',
            'bg-[var(--color-card)] text-sm text-[var(--color-heading)] shadow-[var(--shadow-popover)]',
            contentClassName,
          )}
        >
          {/* overscroll-contain: reaching the end of a long list must not start
              scrolling the page behind the popover. */}
          <SelectPrimitive.Viewport className="max-h-[inherit] overflow-y-auto overscroll-contain p-1">
            {options.map((option, index) => (
              <SelectPrimitive.Item
                key={`${option.value}-${index}`}
                // The option's own value, for tests and for anything that has to pick an
                // option by what it means rather than by the words currently on it.
                data-value={option.value}
                value={toItem(option.value)}
                disabled={option.disabled}
                textValue={option.text}
                className={cn(
                  'relative flex cursor-pointer select-none items-center gap-2 rounded-[6px] px-2 py-1.5 pe-8',
                  'outline-none data-[highlighted]:bg-[var(--color-primary-soft)]',
                  'data-[highlighted]:text-[var(--color-heading)]',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute end-2">
                  <Check className="size-4 text-[var(--color-primary)]" aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

Select.displayName = 'Select'
